import { FetchError, useApi } from '@/hooks/useApi';
import { useConfig } from '@/hooks/useConfig';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { failedPaymentStatuses, successfulPaymentStatuses } from '@/utils/constants';
import { getCodeResponse, GetRegisterResponse, PostRegisterCard, PostRegisterCardResponse } from '@/hooks/card/types';
import { RegisterContext } from '@/hooks/card/useRegister';

export type RegisterContextType = {
  isLoadingPaymentSession: boolean;
  queryErrorPaymentSession: FetchError | null;
  isLoadingAni: boolean;
  queryErrorAni: FetchError | null;
  paymentSession: GetRegisterResponse | undefined;
  ani: getCodeResponse | undefined;
  postRegisterCard: UseMutationResult<PostRegisterCardResponse, FetchError, PostRegisterCard, unknown>;
  registrationCode: string | undefined;
  setRegistrationCode: (registrationCode: string | undefined) => void;
  setPaymentSessionId: (paymentSessionId: string) => void;
  resetPaymentSession: () => void;
  resetRegistrationCode: () => void;
};

export const RegisterProvider = ({ children }: { children: React.ReactNode }) => {
  const { urls } = useConfig();
  const apiFetch = useApi();
  const queryClient = useQueryClient();
  const [registrationCode, setRegistrationCode] = useState<string | undefined>(undefined);
  const [paymentSessionId, setPaymentSessionId] = useState<string | undefined>(undefined);

  const {
    isLoading: isLoadingPaymentSession,
    data: paymentSession,
    error: queryErrorPaymentSession
  } = useQuery<GetRegisterResponse, FetchError>({
    queryKey: ['getRegister', paymentSessionId, urls.akovaPay],
    queryFn: async () => {
      const result = await apiFetch<GetRegisterResponse>(`${urls?.akovaPay}/v1/register/${paymentSessionId}`);
      return result;
    },
    refetchInterval: (data) => {
      if (
        (data.state.data && successfulPaymentStatuses.includes(data.state.data.payment.status?.toLowerCase())) ||
        (data.state.data && failedPaymentStatuses.includes(data.state.data.payment.status?.toLowerCase()))
      ) {
        return false;
      } else {
        return 5000;
      }
    },
    enabled: !!paymentSessionId
  });

  const {
    isLoading: isLoadingAni,
    data: ani,
    error: queryErrorAni
  } = useQuery<getCodeResponse, FetchError>({
    queryKey: ['getAni', registrationCode, urls.akovaPay],
    queryFn: async () => {
      const result = await apiFetch<getCodeResponse>(`${urls?.akovaPay}/v1/register/code/${registrationCode}`);
      return result;
    },
    enabled: !!registrationCode
  });

  const handleResetPaymentSession = () => {
    setPaymentSessionId(undefined);
    queryClient.invalidateQueries({ queryKey: ['getRegister', paymentSessionId, urls.akovaPay] });
  };

  const handleResetRegistrationCode = () => {
    setRegistrationCode(undefined);
    queryClient.invalidateQueries({ queryKey: ['getAni', registrationCode, urls.akovaPay] });
  };

  const postRegisterCard = useMutation<PostRegisterCardResponse, FetchError, PostRegisterCard, unknown>({
    mutationFn: ({
      ani,
      registrationCode,
      deviceType,
      postPaymentRedirectionUrl,
      postPaymentRedirectionFailedUrl,
      postPaymentRedirectionCancelUrl
    }: PostRegisterCard) => {
      return apiFetch<PostRegisterCardResponse>(`${urls?.akovaPay}/v1/register/card`, {
        method: 'POST',
        body: {
          ani,
          registrationCode,
          deviceType,
          postPaymentRedirectionUrl,
          postPaymentRedirectionFailedUrl,
          postPaymentRedirectionCancelUrl
        }
      });
    }
  });

  return (
    <RegisterContext.Provider
      value={{
        isLoadingPaymentSession,
        queryErrorPaymentSession,
        isLoadingAni,
        queryErrorAni,
        ani,
        paymentSession,
        postRegisterCard,
        registrationCode,
        setRegistrationCode,
        setPaymentSessionId,
        resetPaymentSession: handleResetPaymentSession,
        resetRegistrationCode: handleResetRegistrationCode
      }}
    >
      {children}
    </RegisterContext.Provider>
  );
};
